import axios from 'axios';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { AxiosCallError, AxiosCallRequest, AxiosCallSuccess } from './commonActions';
import { endMeetingSession } from './AssesmentRecordingActions';

const theia_ep = get_service_endpoint('theia');

// Action Types
export const CREATE_MEETING_REQUEST = 'CREATE_MEETING_REQUEST';
export const CREATE_MEETING_SUCCESS = 'CREATE_MEETING_SUCCESS';
export const CREATE_MEETING_FAILURE = 'CREATE_MEETING_FAILURE';

export const GET_MEETING_REQUEST = 'GET_MEETING_REQUEST';
export const GET_MEETING_SUCCESS = 'GET_MEETING_SUCCESS';
export const GET_MEETING_FAILURE = 'GET_MEETING_FAILURE';

export const GET_ATTENDEE_REQUEST = 'GET_ATTENDEE_REQUEST';
export const GET_ATTENDEE_SUCCESS = 'GET_ATTENDEE_SUCCESS';
export const GET_ATTENDEE_FAILURE = 'GET_ATTENDEE_FAILURE';

export const UPDATE_MEETING_STATUS_REQUEST = 'UPDATE_MEETING_STATUS_REQUEST';
export const UPDATE_MEETING_STATUS_SUCCESS = 'UPDATE_MEETING_STATUS_SUCCESS';
export const UPDATE_MEETING_STATUS_FAILURE = 'UPDATE_MEETING_STATUS_FAILURE';

export const START_RECORDING_REQUEST = 'START_RECORDING_REQUEST';
export const START_RECORDING_SUCCESS = 'START_RECORDING_SUCCESS';
export const START_RECORDING_FAILURE = 'START_RECORDING_FAILURE';

export const GET_RECORDING_URLS_REQUEST = 'GET_RECORDING_URLS_REQUEST';
export const GET_RECORDING_URLS_SUCCESS = 'GET_RECORDING_URLS_SUCCESS';
export const GET_RECORDING_URLS_FAILURE = 'GET_RECORDING_URLS_FAILURE';

export const CLEAR_MEETING_DATA = 'CLEAR_MEETING_DATA';

// Action Creators
export const createMeetingRequest = () => ({
  type: CREATE_MEETING_REQUEST,
});

export const createMeetingSuccess = meeting => ({
  type: CREATE_MEETING_SUCCESS,
  payload: meeting,
});

export const createMeetingFailure = error => ({
  type: CREATE_MEETING_FAILURE,
  payload: error,
});

export const getMeetingRequest = () => ({
  type: GET_MEETING_REQUEST,
});

export const getMeetingSuccess = meeting => ({
  type: GET_MEETING_SUCCESS,
  payload: meeting,
});

export const getMeetingFailure = error => ({
  type: GET_MEETING_FAILURE,
  payload: error,
});

export const getAttendeeRequest = () => ({
  type: GET_ATTENDEE_REQUEST,
});

export const getAttendeeSuccess = attendee => ({
  type: GET_ATTENDEE_SUCCESS,
  payload: attendee,
});

export const getAttendeeFailure = error => ({
  type: GET_ATTENDEE_FAILURE,
  payload: error,
});

export const updateMeetingStatusRequest = () => ({
  type: UPDATE_MEETING_STATUS_REQUEST,
});

export const updateMeetingStatusSuccess = meeting => ({
  type: UPDATE_MEETING_STATUS_SUCCESS,
  payload: meeting,
});

export const updateMeetingStatusFailure = error => ({
  type: UPDATE_MEETING_STATUS_FAILURE,
  payload: error,
});

export const startRecordingRequest = () => ({
  type: START_RECORDING_REQUEST,
});

export const startRecordingSuccess = recording => ({
  type: START_RECORDING_SUCCESS,
  payload: recording,
});

export const startRecordingFailure = error => ({
  type: START_RECORDING_FAILURE,
  payload: error,
});

export const getRecordingUrlsRequest = () => ({
  type: GET_RECORDING_URLS_REQUEST,
});

export const getRecordingUrlsSuccess = urls => ({
  type: GET_RECORDING_URLS_SUCCESS,
  payload: urls,
});

export const getRecordingUrlsFailure = error => ({
  type: GET_RECORDING_URLS_FAILURE,
  payload: error,
});

export const clearMeetingData = () => ({
  type: CLEAR_MEETING_DATA,
});

// Thunk Actions

/**
 * Create a new meeting for the specified assessment
 * @param {string} assessmentId - The ID of the assessment
 * @returns {Promise} - Promise that resolves with meeting data
 */
export const createMeeting = assessmentId => {
  return async dispatch => {
    dispatch(createMeetingRequest());
    try {
      const url = `${theia_ep}/api/v1/assessments/${assessmentId}/meetings/`;
      const response = await axios.post(url);

      dispatch(createMeetingSuccess(response.data));
      dispatch(AxiosCallSuccess());
      return response.data;
    } catch (error) {
      dispatch(createMeetingFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};

/**
 * Get the active meeting for the specified assessment
 * @param {string} assessmentId - The ID of the assessment
 * @returns {Promise} - Promise that resolves with meeting data
 */
export const getMeeting = assessmentId => {
  return async dispatch => {
    dispatch(getMeetingRequest());
    try {
      const url = `${theia_ep}/api/v1/assessments/${assessmentId}/meetings/`;
      const response = await axios.get(url);

      dispatch(getMeetingSuccess(response.data));
      dispatch(AxiosCallSuccess());
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        // Meeting not found, create a new one
        return dispatch(createMeeting(assessmentId));
      }

      dispatch(getMeetingFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};

/**
 * Get attendee information for a specific meeting
 * @param {string} meetingId - The ID of the meeting
 * @param {string} assessmentId - The ID of the assessment
 * @returns {Promise} - Promise that resolves with attendee data
 */
export const getAttendee = (meetingId, assessmentId) => {
  return async dispatch => {
    dispatch(getAttendeeRequest());
    try {
      const url = `${theia_ep}/api/v1/meetings/${meetingId}/attendee/?assessment_id=${assessmentId}`;
      const response = await axios.get(url);

      dispatch(getAttendeeSuccess(response.data));
      dispatch(AxiosCallSuccess());
      return response.data;
    } catch (error) {
      dispatch(getAttendeeFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};

/**
 * Update the status of an existing meeting
 * @param {string} meetingId - The ID of the meeting
 * @param {string} status - The new status for the meeting
 * @returns {Promise} - Promise that resolves with updated meeting data
 */
export const updateMeetingStatus = (meetingId, status) => {
  return async (dispatch, getState) => {
    dispatch(updateMeetingStatusRequest());
    dispatch(AxiosCallRequest());

    try {
      const url = `${theia_ep}/api/v1/meetings/${meetingId}/`;
      const payload = { status };
      const response = await axios.patch(url, payload);

      dispatch(updateMeetingStatusSuccess(response.data));
      dispatch(AxiosCallSuccess());

      if (status === 'IN') {
        const { meetingSession } = getState().assesmentRecording;
        if (meetingSession) {
          await dispatch(endMeetingSession(meetingSession));
        }
      }

      return response.data;
    } catch (error) {
      dispatch(updateMeetingStatusFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};

/**
 * Start recording for an active meeting
 * @param {string} meetingId - The ID of the meeting
 * @returns {Promise} - Promise that resolves with recording data
 */
export const startRecording = meetingId => {
  return async dispatch => {
    try {
      const url = `${theia_ep}/api/v1/meetings/${meetingId}/recordings/`;
      const response = await axios.post(url);

      dispatch(startRecordingSuccess(response.data));
      dispatch(AxiosCallSuccess());
      return response.data;
    } catch (error) {
      dispatch(startRecordingFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};

/**
 * Get presigned URLs for all recordings associated with an assessment
 * @param {string} assessmentId - The ID of the assessment
 * @returns {Promise} - Promise that resolves with array of recording URLs
 */
export const getRecordingUrls = assessmentId => {
  return async dispatch => {
    dispatch(getRecordingUrlsRequest());
    dispatch(AxiosCallRequest());

    try {
      const url = `${theia_ep}/api/v1/assessments/${assessmentId}/recordings/urls/`;
      const response = await axios.get(url);

      dispatch(getRecordingUrlsSuccess(response.data));
      dispatch(AxiosCallSuccess());
      return response.data;
    } catch (error) {
      dispatch(getRecordingUrlsFailure(error));
      dispatch(AxiosCallError(error));
      throw error;
    }
  };
};
